import { CHARGE_TIMING_OPTIONS, CHARGE_TYPE_OPTIONS } from "./constants";

// Column definitions for the Preview grid — the full visible column set of the legacy where-builder
// grid. Every field in uWMM0012.dfm's qryWMTransactionCharges carrying VisibleOnGrid = True is here
// (43 of them, decoded from the DFM), plus Record # for the KEYSEQUENCE field flagged IsRecordNumber.
// SEQUENCE and USERDEFINEDCOMMENT are the only persistent fields the Delphi grid did NOT show, so they
// are absent here too.
//
// Order follows the DFM field declaration order, which is the order Delphi laid the grid out, except
// Record # is lifted to the front (Architecture.md rule 10 and this app's convention).
//
// dataField casing matches WmtransactionchargeDto's property names exactly — including its
// inconsistent casing (Chargetiming, Chargereleasedate, Quantitybillas alongside ChargeCode,
// RecordNumber) — because the endpoint serialises with DefaultContractResolver, i.e. PascalCase
// straight off the DTO.
//
// dataType is declared on EVERY column, deliberately. DevExtreme only infers it from the first
// non-null value, and plenty of these can come back empty for a whole result set (the charge line's
// own Description is only written when the batch releases a Free Days charge; the user-defined block is
// frequently blank). With nothing to infer from, the column gets no filter-row editor, so the user
// silently loses the ability to filter it.
//
// minWidth on every column is what keeps the grid from squeezing 44 columns into the viewport: with
// columnAutoWidth the total then exceeds the container and DevExtreme shows a horizontal scrollbar
// instead of compressing everything to unreadable slivers.

// AMOUNT is the ONLY currency column: uWMM0012.pas:181 declares it TAbCurrencyField while RATE,
// FOREIGNRATE, FOREIGNAMOUNT, QUANTITYBILLAS and USERDEFINEDQUANTITY_1..5 are all TAbFloatField
// (lines 179, 194, 202, 206-207). AI8 renders them "$0.00" vs "1.50" accordingly — note the currency
// symbol sits flush against the digits, with no space.
const MONEY_FORMAT = "$#,##0.00";
const TWO_DECIMAL_FORMAT = { type: "fixedPoint", precision: 2, useGrouping: true };

// Date formats are chosen to match the Delphi grid exactly (uWMM0012 preview), which is why they are
// UNPADDED: AI8 renders "5/22/2013" and "8/18/2026 5:32:47 AM", not "05/22/2013". Note this differs
// from the app's more common "MM/dd/yyyy" / "MM/dd/yyyy hh:mm:ss a" column formats, deliberately.
//
// Which fields carry a time is also taken from AI8 rather than from the column's CLR type — every one of
// these is a DateTime in the DTO, but the Delphi data dictionary presents the audit stamps and the
// transaction date as timestamps and Charge Release Date as a plain date.
const DATE_FORMAT = "M/d/yyyy";
const DATETIME_FORMAT = "M/d/yyyy h:mm:ss a";

const TEXT = "string";
const NUMBER = "number";
const DATE = "date";
const DATETIME = "datetime";

// Widths tuned to the caption plus a plausible value, so nothing is truncated at the default zoom.
const W_CODE = 120;
const W_NAME = 200;
const W_DESC = 180;
const W_DATE = 120;
const W_DATETIME = 165;
const W_NUMBER = 120;
const W_MONEY = 130;
const W_SHORT = 90;

export const PREVIEW_KEY_EXPR = "Rowid";

const text = (dataField, caption, width = W_CODE) => ({
    dataField,
    caption,
    dataType: TEXT,
    minWidth: width,
});

/** A plain date, no time component shown — Delphi: "1/1/2025". */
const date = (dataField, caption) => ({
    dataField,
    caption,
    dataType: DATE,
    format: DATE_FORMAT,
    minWidth: W_DATE,
});

/** A timestamp, seconds included — Delphi: "8/18/2026 5:32:47 AM". */
const dateTime = (dataField, caption) => ({
    dataField,
    caption,
    dataType: DATETIME,
    format: DATETIME_FORMAT,
    minWidth: W_DATETIME,
});

const number = (dataField, caption, format = TWO_DECIMAL_FORMAT, width = W_NUMBER) => ({
    dataField,
    caption,
    dataType: NUMBER,
    format,
    minWidth: width,
});

/** The five numbered user-defined blocks the Delphi grid shows (Code / Date / Quantity 1..5). */
const userDefinedBlock = (prefix, caption, factory) =>
    [1, 2, 3, 4, 5].map((n) => factory(`${prefix}${n}`, `${caption} ${n}`));

export const PREVIEW_COLUMNS = [
    // Technical key — never shown, and not offered in the column chooser either (rule 8).
    { dataField: "Rowid", dataType: TEXT, visible: false, showInColumnChooser: false },

    { ...text("RecordNumber", "Record #", W_SHORT) },

    text("Identityid", "Customer"),
    text("Name", "Name", W_NAME),
    text("TransactionNumber", "Transaction #"),
    // AI8 shows this with the time and seconds ("5/22/2013 2:00:29 PM"), not as a bare date.
    dateTime("TransactionDate", "Transaction Date"),
    text("ChargeCode", "Charge"),
    text("ChargeDescription1", "Charge Desc 1", W_DESC),
    text("ChargeDescription2", "Charge Desc 2", W_DESC),

    dateTime("Creationdatetime", "Creation Date"),
    text("Creationuserid", "Created By"),
    dateTime("Lastupdatedatetime", "Last Updated"),
    text("Lastupdateuserid", "Last Updated By"),

    // The three framework-managed columns. Delphi showed them, but all three are pinned by both the
    // Preview SELECT and the stored procedure (company / 'R' / 'WM'), so every row carries the same
    // value and the columns say nothing. Off by default, still reachable through the column chooser.
    { ...text("Companyid", "Company ID", W_SHORT), visible: false },
    { ...text("Module", "Module", W_SHORT), visible: false },
    { ...text("Recordtype", "Record Type", W_SHORT), visible: false },

    // Rule 3: coded fields render descriptively, not as a raw I/T or F/G.
    {
        ...text("Chargetype", "Charge Type", W_NAME),
        lookup: { dataSource: CHARGE_TYPE_OPTIONS, displayExpr: "text", valueExpr: "id" },
    },
    number("Quantitybillas", "Bill As"),
    text("Quantitybillasuom", "UOM", W_SHORT),
    number("Amount", "Amount", MONEY_FORMAT, W_MONEY),
    text("Status", "Status", W_SHORT),
    {
        ...text("Chargetiming", "Charge Timing", W_NAME),
        lookup: { dataSource: CHARGE_TIMING_OPTIONS, displayExpr: "text", valueExpr: "id" },
    },

    ...userDefinedBlock("Userdefinedcode", "Code", text),
    // AI8 shows these with the time and seconds ("8/18/2026 5:50:50 AM"), unlike Charge Release Date.
    ...userDefinedBlock("Userdefineddate", "Date", dateTime),
    ...userDefinedBlock("Userdefinedquantity", "Quantity", (field, caption) => number(field, caption)),

    // AI8 shows this as a bare date ("1/1/2025"). Rendering it as a timestamp added a meaningless
    // ", 12:00 AM" to every row, since the column is date-grained in practice.
    date("Chargereleasedate", "Charge Release Date"),
    text("Description", "Description", W_DESC),
    number("Rate", "Rate"),
    text("Userdefinedreference1", "Reference - 1", W_DESC),
    text("Generationmethod", "Generation Method"),
    text("Userdefinedreference2", "Reference - 2", W_DESC),
    // The DFM gives these the same DisplayLabels as RATE / AMOUNT; qualified so the two pairs are
    // distinguishable in the column chooser and the export.
    // Both are TAbFloatField, NOT currency — only AMOUNT above carries the "$".
    number("Foreignrate", "Foreign Rate"),
    number("Foreignamount", "Foreign Amount"),
];
